/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import Helmet from 'react-helmet';
import IcomoonReact from 'icomoon-react';
import { Prompt } from 'react-router-dom';
import ResizeObserver from 'react-resize-observer';

import AnalysisComponent from './components/presentational/AnalysisComponent';
import OverviewComponent from './components/presentational/OverviewComponent';
import NewDashboardComponent from './NewDashboardComponent';
import SharedTemplateTriOptionMenuButton from '../sports-science/components/presentational/sharedTemplateComponents/SharedTemplateTriOptionMenuButton';
import SharedTemplatesGrid from '../sports-science/components/presentational/sharedTemplateComponents/SharedTemplatesGrid';
import TemplateSearchBar from '../sports-science/components/presentational/sharedTemplateComponents/TemplateSearchBar';
import Icon from '../../shared/_TBComponents/Icon';
import HeaderText from '../../shared/_TBComponents/HeaderText';
import {
  clearSCDashData,
  fetchGroups,
  fetchTemplates,
  setAboutDashboardModalShowing,
  setActiveTemplate,
  setDeleteDashboardModal,
  setDuplicateModalShowing,
  setEditDashboardModal,
  setExercises,
  setGroups,
  setIsNewDashboard,
  setIsOverview,
  setIsUnsavedDashModalShowing,
  setNextLocation,
  setSCDashView,
  setShareDashboardModal,
} from './ducks/sportCoachDashboardActions';
import { getModuleInfoByTitle } from '../sports-science/components/presentational/packages';
import iconSet from '../../shared/images/teambuildr-selection.json';
import { TransitionGlobal } from '../../shared/GlobalStyles';
import { dashboardTracker } from '../../shared/utils/amplitudeHelper';

const moduleInfo = getModuleInfoByTitle('KPI Tracker');

const BackButtonContainer = styled('div')`
  cursor: pointer;
`;

const Container = styled('div')`
  width: 100%;
`;

const DashboardHeader = styled('div')`
  align-items: flex-end;
  background-color: ${(props) => props.bgColor};
  display: flex;
  justify-content: space-between;
  padding-left: 20px;
  padding-top: 30px;
  width: 100%;
`;

const HeaderAndSubheader = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-start;
  margin-left: 10px;
`;

const HeaderTextAndArrow = styled('div')`
  align-items: center;
  display: flex;
  padding-bottom: 8px;
`;

const HeaderWidthContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const IconsAndSearch = styled('div')`
  align-items: center;
  display: flex;
`;

const InfoWrapper = styled('button')`
  align-items: center;
  background: none;
  border: 2px solid #9e9e9e;
  border-radius: 50%;
  cursor: ${(props) => (props.disabled ? 'cursor' : 'pointer')};
  display: flex;
  height: 20px;
  justify-content: center;
  padding: 0;
  transition: ${TransitionGlobal};
  width: 20px;

  :hover {
    opacity: ${(props) => (props.disabled ? '1' : '.7')};
  }

  @media only screen and (max-width: 601px) {
    display: none;
  }
`;

const MenuContainer = styled('div')`
  display: flex;
`;

const MenuItem = styled('button')`
  border: none;
  border-bottom: ${(props) => (props.isActive ? '3px solid #1787FB!important' : '3px solid white')};
  color: ${(props) => (!props.isActive ? '#9E9E9E' : '#444444')};
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  margin-left: 10px;
  margin-right: 12px;
`;

const PageWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const SubheaderText = styled('h2')`
  color: #9e9e9e;
  font-family: 'Nunito Sans';
  font-size: 16px;
  line-height: 24px;
  margin-bottom: 0;
`;

const SportCoachDashboard = ({ handleViewInfoModal }) => {
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const {
    isNewDashboard, nextLocation, template, templates,
  } = useSelector((state) => state.sportCoachDashboard.data);
  const { isOverview, scDashView } = useSelector(
    (state) => state.sportCoachDashboard.ui,
  );

  const [containerWidth, setContainerWidth] = useState(0);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedExercises, setSelectedExercises] = useState([]);

  const pageRef = useRef(null);
  const searchInputRef = useRef(null);

  const dispatch = useDispatch();
  const theme = useTheme();

  const unsavedDash = isOverview && !template;

  const handleContainerClick = (e) => {
    if (
      e.target.id !== 'sc-dash-search-container'
      && e.target.id !== 'sc-dash-search-icon-container'
      && e.target.id !== 'sc-dash-search-container-input'
      && e.target.id !== 'sc-dash-mag-icon'
      && isSearchFocused
      && searchTerm.length === 0
    ) {
      setIsSearchFocused(false);
      if (searchInputRef?.current) {
        searchInputRef?.current?.blur();
      }
    }
  };

  const handleCreateNewDashboard = () => {
    dispatch(setIsNewDashboard(true));
    dashboardTracker(
      'Sport Coach Dashboard',
      'New Dashboard',
      'Create new dash clicked',
    );
  };

  const handleFocusSearch = () => {
    setIsSearchFocused(true);
    setTimeout(() => {
      if (searchInputRef?.current) {
        searchInputRef?.current?.focus();
      }
    }, 0);
  };

  const handleInfoClick = () => {
    handleViewInfoModal(moduleInfo);
  };

  const handleMenuClick = (view) => {
    if (view !== scDashView) {
      dispatch(setSCDashView(view));
      dashboardTracker(
        'Sport Coach Dashboard',
        view === 'group' ? 'Group Analysis' : 'Performance Overview',
        'Menu item clicked',
      );
    }
  };

  const handleGroupMenuClick = () => {
    handleMenuClick('group');
  };

  const handleNavigation = (location) => {
    if (unsavedDash && !nextLocation) {
      batch(() => {
        dispatch(setNextLocation(location.pathname));
        dispatch(setIsUnsavedDashModalShowing(true));
      });
      return false;
    }
    return true;
  };

  const handlePerformanceMenuClick = () => {
    handleMenuClick('performance');
  };

  const handleResize = (rect) => {
    setContainerWidth(rect?.width);
  };

  const handleReturnClick = () => {
    if (isNewDashboard) {
      batch(() => {
        dispatch(setIsNewDashboard(false));
        setSelectedExercises([]);
      });
      dashboardTracker(
        'Sport Coach Dashboard',
        'Back Arrow',
        'Return to main screen clicked from new dashboard form',
      );
    } else if (unsavedDash) {
      batch(() => {
        dispatch(setIsNewDashboard(true));
        dispatch(setIsOverview(false));
      });
      dashboardTracker(
        'Sport Coach Dashboard',
        'Back Arrow',
        'Return to form clicked from unsaved dashboard',
      );
    } else {
      batch(() => {
        dispatch(setIsOverview(!isOverview));
        dispatch(clearSCDashData());
      });
      dashboardTracker(
        'Sport Coach Dashboard',
        'Back Arrow',
        'Return to main screen clicked from saved dashboard template',
      );
    }
  };

  const handleTemplateView = (templateObj) => {
    dashboardTracker(
      'Sport Coach Dashboard',
      'Main Screen',
      'Saved dashboard opened',
    );
    batch(() => {
      dispatch(setExercises(templateObj.exercises));
      dispatch(setGroups(templateObj.groups));
      dispatch(setActiveTemplate(templateObj));
      dispatch(setIsOverview(true));
    });
  };

  const scrollTop = () => {
    setTimeout(
      () => pageRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      }),
      250,
    );
  };

  useEffect(() => {
    dispatch(clearSCDashData());
  }, [dispatch]);

  useEffect(() => {
    if (Object.keys(currentUser).length) {
      dispatch(fetchTemplates(currentUser.accountCode));
    }
  }, [currentUser, dispatch]);

  useEffect(() => {
    if (!isOverview && currentUser) {
      dispatch(fetchGroups(currentUser.accountCode));
    }
  }, [isOverview, currentUser, dispatch]);

  useEffect(() => {
    if (isOverview) {
      scrollTop();
    }
  }, [isOverview]);

  useEffect(() => {
    if (!isNewDashboard && !isOverview) {
      setSelectedExercises([]);
    }
  }, [isNewDashboard, isOverview]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      const customMessage = `This dashboard has not been saved, are you sure you want to exit without saving?
      Exiting now will discard any unsaved progress. If you have made any updates,
      close this modal to review and save your changes.`;
      return customMessage;
    };

    if (unsavedDash) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [unsavedDash]);

  const templateActions = {
    clearActiveTemplate: clearSCDashData,
    handleCreateNewDashboard,
    handleTemplateView,
    setAboutModal: setAboutDashboardModalShowing,
    setDeleteModal: setDeleteDashboardModal,
    setDuplicateModal: setDuplicateModalShowing,
    setEditModal: setEditDashboardModal,
    setShareModal: setShareDashboardModal,
  };

  const renderMenu = () => {
    if (!isOverview) {
      return (
        <MenuContainer>
          <IconsAndSearch>
            <MenuItem>
              <TemplateSearchBar
                handleClick={handleFocusSearch}
                iconContainerId='sc-dash-search-icon-container'
                iconId='sc-dash-mag-icon'
                id='sc-dash-search-container'
                isFocused={isSearchFocused}
                searchId='sc-dash-search-container-input'
                searchRef={searchInputRef}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
              />
            </MenuItem>
          </IconsAndSearch>
        </MenuContainer>
      );
    }

    return (
      <MenuContainer>
        <MenuItem
          isActive={scDashView === 'performance'}
          onClick={handlePerformanceMenuClick}
        >
          Performance Overview
        </MenuItem>
        <MenuItem
          isActive={scDashView === 'group'}
          onClick={handleGroupMenuClick}
        >
          Group Analysis
        </MenuItem>
      </MenuContainer>
    );
  };

  const renderHeader = () => (
    <DashboardHeader bgColor={theme.colors.background[0]}>
      <HeaderWidthContainer>
        <HeaderTextAndArrow>
          {isOverview || isNewDashboard ? (
            <BackButtonContainer>
              <IcomoonReact
                icon='left-arrow'
                iconSet={iconSet}
                onClick={handleReturnClick}
                size={20}
              />
            </BackButtonContainer>
          ) : null}
          <HeaderAndSubheader>
            <HeaderText fontWeight='300' letterSpacing='normal'>
              KPI Tracker
            </HeaderText>
            {containerWidth > 760 && (
              <SubheaderText>(Formerly Sport Coach Dashboard)</SubheaderText>
            )}
            <InfoWrapper
              aria-label='Show dashboard onboarding modal'
              onClick={handleInfoClick}
            >
              <Icon
                icon='info'
                size='12px'
                styledProps={{
                  display: 'flex',
                }}
              />
            </InfoWrapper>
          </HeaderAndSubheader>
        </HeaderTextAndArrow>
        {renderMenu()}
      </HeaderWidthContainer>
    </DashboardHeader>
  );

  const renderContent = () => {
    if (!isOverview && !isNewDashboard) {
      return (
        <SharedTemplatesGrid
          actions={templateActions}
          dashboard='KPI Tracker'
          legacyName='Sport Coach'
          searchTerm={searchTerm}
          templates={templates}
        />
      );
    }

    if (isOverview && scDashView === 'performance') {
      return <OverviewComponent />;
    }

    if (isOverview && scDashView === 'group') {
      return <AnalysisComponent />;
    }

    if (!isOverview && isNewDashboard) {
      return (
        <NewDashboardComponent
          selectedExercises={selectedExercises}
          setSelectedExercises={setSelectedExercises}
        />
      );
    }

    return null;
  };

  return (
    <Container onClick={handleContainerClick}>
      <ResizeObserver onResize={handleResize} />
      <Helmet>
        <title>KPI Tracker | TeamBuildr</title>
      </Helmet>
      <PageWrapper ref={pageRef}>
        {renderHeader()}
        {renderContent()}
        {isOverview && (
          <SharedTemplateTriOptionMenuButton
            dashboard='KPI Tracker'
            legacyName='KPI Tracker'
          />
        )}
      </PageWrapper>
      <Prompt message={handleNavigation} when={unsavedDash} />
    </Container>
  );
};

export default SportCoachDashboard;
